import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";

import { type ClientNutritionWeek } from "@fitness-app/data-models/entities/ClientNutritionWeek";
import { type HydrationEntry } from "@fitness-app/data-models/entities/HydrationEntry";
import { type Measurement } from "@fitness-app/data-models/entities/Measurement";
import { type DiscriminateUnion } from "@fitness-app/utils/src/types/utils";

import { traineeKeys, useTraineeId } from "~/modules/Trainee/TraineeProfile/scope";
import { traineeMeasurementKeys } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/useTraineeMeasurementsQueries";
import { type CommonChartProps } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/types";
import { supabase } from "~/store/initializeStore";

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

const getRangeBounds = (range: CommonChartProps["range"]) =>
  range?.[0] && range?.[1]
    ? { dateStart: range[0].startOf("day").toISOString(), dateEnd: range[1].endOf("day").toISOString() }
    : null;

/** Pomiary jednego typu z wybranego zakresu (to samo co `fetchMeasurementsForRangeAndType`). */
export const useMeasurementsForRange = <T extends Measurement["type"]>(type: T, range: CommonChartProps["range"]) => {
  const traineeId = useTraineeId();
  const bounds = getRangeBounds(range);

  return useQuery({
    queryKey: traineeMeasurementKeys.forRange(traineeId, type, bounds?.dateStart ?? "", bounds?.dateEnd ?? ""),
    enabled: Boolean(bounds),
    queryFn: async () => {
      const { error, data } = await supabase
        .from("measurement")
        .select("*")
        .order("eventTimestamp")
        .eq("traineeId", traineeId)
        .eq("type", type)
        .gte("eventTimestamp", bounds!.dateStart)
        .lte("eventTimestamp", bounds!.dateEnd)
        .returns<DiscriminateUnion<Measurement, "type", T>[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    },
  });
};

const HYDRATION_ENTRIES_PAGE = 1000;

/**
 * TRE-2413: wszystkie napoje dodane w apce w zakresie dni. PostgREST ucina odpowiedź na 1000
 * wierszach, a pominięta kawa policzyłaby się w rozbiciu jako woda, więc stronicujemy po id.
 */
const fetchHydrationEntries = async (traineeId: string, dayStart: string, dayEnd: string) => {
  const entries: HydrationEntry[] = [];

  for (let from = 0; ; from += HYDRATION_ENTRIES_PAGE) {
    const { error, data } = await supabase
      .from("hydration_entry")
      .select("*")
      .eq("traineeId", traineeId)
      .gte("eventDate", dayStart)
      .lte("eventDate", dayEnd)
      .order("id")
      .range(from, from + HYDRATION_ENTRIES_PAGE - 1)
      .returns<HydrationEntry[]>();

    if (error) {
      throw new Error(error.message);
    }

    entries.push(...(data ?? []));
    if (!data || data.length < HYDRATION_ENTRIES_PAGE) {
      return entries;
    }
  }
};

/** TRE-2413: napoje dodane w apce w wybranym zakresie dni (do rozbicia nawodnienia). */
export const useHydrationEntriesForRange = (range: CommonChartProps["range"]) => {
  const traineeId = useTraineeId();
  const dayStart = range?.[0]?.format("YYYY-MM-DD") ?? "";
  const dayEnd = range?.[1]?.format("YYYY-MM-DD") ?? "";

  return useQuery({
    queryKey: traineeMeasurementKeys.hydrationEntries(traineeId, dayStart, dayEnd),
    enabled: Boolean(dayStart && dayEnd),
    queryFn: () => fetchHydrationEntries(traineeId, dayStart, dayEnd),
  });
};

/** TRE-2413: napoje z jednego dnia (kalendarz). */
export const useHydrationEntriesForDay = (traineeId: string, eventDate: string) =>
  useQuery({
    queryKey: traineeMeasurementKeys.hydrationEntries(traineeId, eventDate, eventDate),
    queryFn: () => fetchHydrationEntries(traineeId, eventDate, eventDate),
  });

/** Tygodnie diety z wybranego zakresu (to samo co `fetchNutritionSummaryForRange`). */
export const useNutritionSummaryForRange = (range: CommonChartProps["range"]) => {
  const traineeId = useTraineeId();
  const bounds = getRangeBounds(range);

  return useQuery({
    queryKey: [...traineeKeys.all(traineeId), "nutritionSummary", bounds?.dateStart ?? "", bounds?.dateEnd ?? ""],
    enabled: Boolean(bounds),
    queryFn: async () => {
      const startDate = dayjs(bounds!.dateStart);
      const endDate = dayjs(bounds!.dateEnd);

      const startWeek = startDate.format("YYYY-WW");
      let endWeek = endDate.format("YYYY-WW");

      if (endWeek.endsWith("01")) {
        endWeek = endDate.subtract(1, "week").format("YYYY-WW");
      }

      const { error, data } = await supabase
        .from("client_nutrition_week")
        .select("*")
        .order("eventWeekIso")
        .eq("traineeId", traineeId)
        .gte("eventWeekIso", startWeek)
        .lte("eventWeekIso", endWeek)
        .returns<ClientNutritionWeek[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    },
  });
};
